import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import { formatTimestamp } from "../helpers/date.helper";
import { AuditLog, AuditLogsPage, getAuditLogs } from "../services/audit.service";
import { toggleAlert } from "../store/resuders/app.reducer";
import { useAppDispatch } from "../store/hooks";
import { DataGrid } from "../ui";

export const AuditLogs = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [data, setData] = useState<AuditLogsPage | null>(null);
    const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
    const [selectedRow, setSelectedRow] = useState<AuditLog | null>(null);
    const [criteria, setCriteria] = useState({
        sort: "-createdAt",
        pageNo: 0,
        pageSize: 20,
    });

    const getCellWrapperSx = (row: AuditLog) => ({
        width: "100%",
        backgroundColor: hoveredRowId === row.id ? "#EAF4F4" : "transparent",
        cursor: "pointer",
    });

    const handleRowHover = (row: AuditLog | null) => {
        setHoveredRowId(row ? row.id : null);
    };

    const handleRowClick = (row: AuditLog) => {
        setSelectedRow(row);
    };

    const renderTruncatedCell = (row: AuditLog, value: string | number | undefined, maxWidth = "220px") => {
        const text = value === undefined || value === null || value === "" ? "-" : String(value);
        return (
            <Box sx={getCellWrapperSx(row)} onMouseEnter={() => handleRowHover(row)} onMouseLeave={() => handleRowHover(null)} onClick={() => handleRowClick(row)}>
                <Tooltip title={text}>
                    <Typography
                        component="span"
                        sx={{ display: "inline-block", maxWidth, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {text}
                    </Typography>
                </Tooltip>
            </Box>
        );
    };

    const renderCell = (row: AuditLog, value: string | number | undefined, maxWidth = "220px") => {
        const text = value === undefined || value === null || value === "" ? "-" : String(value);
        return (
            <Box sx={getCellWrapperSx(row)} onMouseEnter={() => handleRowHover(row)} onMouseLeave={() => handleRowHover(null)} onClick={() => handleRowClick(row)}>
                <Typography component="span" sx={{ display: "inline-block", maxWidth, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {text}
                </Typography>
            </Box>
        );
    };

    const columns: any[] = [
        {
            caption: t("ID"),
            width: "5%",
            fieldName: "id",
            sortable: true,
            redner: (row: AuditLog) => renderCell(row, row.id, "60px"),
        },
        {
            caption: t("Created At"),
            width: "15%",
            fieldName: "createdAt",
            sortable: true,
            redner: (row: AuditLog) => renderCell(row, formatTimestamp(row.createdAt), "170px"),
        },
        {
            caption: t("Method"),
            width: "8%",
            fieldName: "method",
            sortable: true,
            redner: (row: AuditLog) => renderCell(row, row.method === "PUT" ? "UPDATE" : row.method === "POST" ? "CREATE" : row.method, "90px"),
        },
        {
            caption: t("Operation"),
            width: "20%",
            fieldName: "operation",
            sortable: true,
            redner: (row: AuditLog) => renderTruncatedCell(row, row.operation, "260px"),
        },
        {
            caption: t("Request URI"),
            width: "23%",
            fieldName: "requestUri",
            sortable: true,
            redner: (row: AuditLog) => renderTruncatedCell(row, row.requestUri, "320px"),
        },
        {
            caption: t("Status"),
            width: "7%",
            fieldName: "statusCode",
            sortable: true,
            redner: (row: AuditLog) => renderCell(row, row.statusCode, "70px"),
        },
        {
            caption: t("IP Address"),
            width: "10%",
            fieldName: "ipAddress",
            sortable: true,
            redner: (row: AuditLog) => renderTruncatedCell(row, row.ipAddress, "140px"),
        },
        {
            caption: t("User"),
            width: "12%",
            fieldName: "username",
            sortable: true,
            redner: (row: AuditLog) => renderTruncatedCell(row, row.username, "190px"),
        },
    ];

    const sortChanged = (column: string, direction: string) => {
        setCriteria((prev) => ({
            ...prev,
            pageNo: 0,
            sort: `${direction === "ASC" ? "+" : "-"}${column}`,
        }));
    };

    const pageChanged = (page: number) => {
        setCriteria((prev) => ({
            ...prev,
            pageNo: page - 1,
        }));
    };

    const pageSizeChanged = (size: number) => {
        setCriteria((prev) => ({
            ...prev,
            pageSize: size,
            pageNo: 0,
        }));
    };

    const loadData = async () => {
        const sortBy = criteria.sort.substring(1) || "createdAt";
        const sortDir = criteria.sort.charAt(0) === "+" ? "asc" : "desc";
        const response = await getAuditLogs({
            pageNo: criteria.pageNo,
            pageSize: criteria.pageSize,
            sortBy,
            sortDir,
        });

        if (["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST", "ERR_NETWORK"].includes(response?.code)) {
            dispatch(toggleAlert(response?.response?.data?.data?.messages?.join(",") || "Failed to load audit logs"));
            return;
        }

        setData(response?.data?.data);
    };

    useEffect(() => {
        loadData();
    }, [criteria]);

    return (
        <DndProvider backend={HTML5Backend}>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    className="flex justify-end align-center gap-8"
                    display="flex"
                    justifyContent="space-between"
                    marginBottom="16px"
                >
                    <div className="flex-center mb-16 pl-20 mt-16">
                        <Tooltip title={t("Refresh")}>
                            <IconButton onClick={loadData} size="small" sx={{ mr: 1 }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <ManageHistoryIcon />
                        <Typography variant="subtitle2">{t("Audit Logs")}</Typography>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <Card className="h-full">
                        <CardContent>
                            <DataGrid
                                pageChanged={pageChanged}
                                totalPages={data?.totalPages || 0}
                                sortChanged={sortChanged}
                                columns={columns}
                                data={data?.content || []}
                                sortBy={criteria.sort}
                                pageSizeChanged={pageSizeChanged}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t("Audit Log Details")}</DialogTitle>
                <DialogContent>
                    {selectedRow &&
                        Object.entries(selectedRow).map(([key, value]) => (
                            <Grid container key={key} sx={{ py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
                                <Grid item xs={4}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {key}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2">
                                        {key === "createdAt" ? formatTimestamp(String(value)) : value === undefined || value === null || value === "" ? "-" : String(value)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedRow(null)} variant="outlined">
                        {t("Close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </DndProvider>
    );
};
